import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Map, Marker, Popup } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './TaxiMap.css';

const TaxiMap = ({ driver, tripDetails }) => {
  const mapRef = useRef(null);
  const [hoveredMarker, setHoveredMarker] = useState(null);
  
  // Find pickup and dropoff locations
  const pickupLocation = tripDetails.find(location => location.locationType === 'pickup');
  const dropoffLocation = tripDetails.find(location => location.locationType === 'dropoff');
  
  // Extract rider information if available
  const riderInfo = useMemo(() => driver?.rider_info || {}, [driver]);
  const hasRiderLocation = riderInfo.latitude !== null && riderInfo.latitude !== undefined && 
                          riderInfo.longitude !== null && riderInfo.longitude !== undefined;
  
  // Calculate center for initial view
  const calculateCenter = () => {
    // Prioritize rider location if available
    if (hasRiderLocation) {
      return [riderInfo.longitude, riderInfo.latitude];
    }
    
    // Fallback to default center calculation
    if (!pickupLocation || !dropoffLocation) {
      return [driver.longitude, driver.latitude];
    }
    
    const centerLng = (pickupLocation.longitude + dropoffLocation.longitude) / 2;
    const centerLat = (pickupLocation.latitude + dropoffLocation.latitude) / 2;
    
    return [centerLng, centerLat];
  };
  
  const [center] = useState(calculateCenter());
  
  useEffect(() => {
    // When map loads, prioritize rider location if available
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      
      if (hasRiderLocation) {
        // Focus on rider location
        map.flyTo({
          center: [riderInfo.longitude, riderInfo.latitude],
          zoom: 15,
          essential: true
        });
      } else if (pickupLocation && dropoffLocation) {
        // Otherwise fit bounds to show all markers
        map.fitBounds(
          [
            [pickupLocation.longitude, pickupLocation.latitude],
            [dropoffLocation.longitude, dropoffLocation.latitude]
          ],
          { padding: 60, maxZoom: 13 }
        );
      }
    }
  }, [pickupLocation, dropoffLocation, hasRiderLocation, riderInfo]);
  
  // Custom marker elements
  const renderPickupMarker = () => {
    if (!pickupLocation) return null;
    
    return (
      <Marker 
        latitude={pickupLocation.latitude}
        longitude={pickupLocation.longitude}
        anchor="center"
      >
        <div 
          className="custom-marker pickup-marker"
          onMouseEnter={() => setHoveredMarker('pickup')}
          onMouseLeave={() => setHoveredMarker(null)}
        ></div>
      </Marker>
    );
  };
  
  const renderDropoffMarker = () => {
    if (!dropoffLocation) return null;
    
    return (
      <Marker 
        latitude={dropoffLocation.latitude}
        longitude={dropoffLocation.longitude}
        anchor="center"
      >
        <div 
          className="custom-marker dropoff-marker"
          onMouseEnter={() => setHoveredMarker('dropoff')}
          onMouseLeave={() => setHoveredMarker(null)}
        ></div>
      </Marker>
    );
  };

  // Render rider location marker if available
  const renderRiderMarker = () => {
    if (!hasRiderLocation) return null;
    
    return (
      <Marker 
        latitude={riderInfo.latitude}
        longitude={riderInfo.longitude}
        anchor="center"
      >
        <div 
          className="custom-marker rider-marker"
          onMouseEnter={() => setHoveredMarker('rider')}
          onMouseLeave={() => setHoveredMarker(null)}
        >
          <i className="fas fa-user"></i>
        </div>
      </Marker>
    );
  };

  // Render driver location marker (taxi icon)
  const renderDriverMarker = () => {
    // Only render if driver location coordinates are available
    if (!driver || driver.latitude == null || driver.longitude == null) return null;
    
    return (
      <Marker 
        latitude={driver.latitude}
        longitude={driver.longitude}
        anchor="center"
      >
        <div 
          className="custom-marker driver-marker"
          onMouseEnter={() => setHoveredMarker('driver')}
          onMouseLeave={() => setHoveredMarker(null)}
        >
          <i className="fas fa-taxi"></i>
        </div>
      </Marker>
    );
  };
  
  return (
    <div className="map-wrapper">
      <Map
        ref={mapRef}
        willReadFrequently={true}
        initialViewState={{
          latitude: center[1],
          longitude: center[0],
          zoom: hasRiderLocation ? 15 : 12
        }}
        style={{ width: "100%", height: "100%" }}
        mapStyle={"mapbox://styles/mapbox/streets-v12"}
        mapboxAccessToken="pk.eyJ1IjoicmVrYXIiLCJhIjoiY2swYjI1czZzMG5xczNubXd4ODZ5ZmdvNyJ9.UUIH_qL1wzfGoFX2eG6Cyg"
        attributionControl={false}
      >
        {/* Markers */}
        {renderPickupMarker()}
        {renderDropoffMarker()}
        {renderDriverMarker()}
        {renderRiderMarker()}
        
        {/* Popups */}
        {hoveredMarker === 'pickup' && pickupLocation && (
          <Popup
            latitude={pickupLocation.latitude}
            longitude={pickupLocation.longitude}
            closeButton={false}
            closeOnClick={false}
            anchor="top"
            offset={15}
          >
            <div className="popup-content pickup">PICKUP</div>
          </Popup>
        )}
        
        {hoveredMarker === 'dropoff' && dropoffLocation && (
          <Popup
            latitude={dropoffLocation.latitude}
            longitude={dropoffLocation.longitude}
            closeButton={false}
            closeOnClick={false}
            anchor="top"
            offset={15}
          >
            <div className="popup-content dropoff">DROPOFF</div>
          </Popup>
        )}

        {hoveredMarker === 'driver' && driver && driver.latitude && driver.longitude && (
          <Popup
            latitude={driver.latitude}
            longitude={driver.longitude}
            closeButton={false}
            closeOnClick={false}
            anchor="top"
            offset={15}
          >
            <div className="popup-content driver">DRIVER</div>
          </Popup>
        )}

        {hoveredMarker === 'rider' && hasRiderLocation && (
          <Popup
            latitude={riderInfo.latitude}
            longitude={riderInfo.longitude}
            closeButton={false}
            closeOnClick={false}
            anchor="top"
            offset={15}
          >
            <div className="popup-content rider">YOU</div>
          </Popup>
        )}
      </Map>
    </div>
  );
};

export default TaxiMap;
